import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { TooltipProps } from "recharts";
import type { PulsePoint } from "@/api/types";
import { weekShort, fmt } from "@/lib/format";
import { THEME } from "@/lib/theme";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

export interface ActiveUsersBarsProps {
  data: PulsePoint[];
}

type ChartTooltipProps = TooltipProps<number, string>;

function BarsTooltip({ active, payload, label }: ChartTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0];
  const raw = point?.value;
  const value = typeof raw === "number" ? raw : Number(raw ?? 0);
  const dateLabel = typeof label === "string" ? weekShort(label) : String(label ?? "");
  return (
    <div
      className="rounded-xl border border-white/10 bg-bg-1/80 px-3 py-2 text-xs shadow-lg backdrop-blur-md"
      style={{ minWidth: 110 }}
    >
      <div className="text-[10px] uppercase tracking-wider text-text-4">
        {dateLabel}
      </div>
      <div className="tabular mt-1 text-sm font-semibold text-text-1">
        {fmt(value)}{" "}
        <span className="text-[10px] font-normal uppercase tracking-wider text-text-4">
          usuarios
        </span>
      </div>
    </div>
  );
}

/** Bar chart for "Usuarios activos por mes". Vertical brand gradient,
 * rise-in animation on mount, and a hover spotlight that dims non-focused
 * bars to 40% opacity. */
export function ActiveUsersBars({ data }: ActiveUsersBarsProps) {
  const reducedMotion = usePrefersReducedMotion();
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart
        data={data}
        margin={{ top: 12, right: 12, bottom: 0, left: 0 }}
        onMouseLeave={() => setHoverIdx(null)}
      >
        <defs>
          <linearGradient id="grad-bars" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={THEME.brand.end} stopOpacity={0.95} />
            <stop offset="100%" stopColor={THEME.brand.start} stopOpacity={0.85} />
          </linearGradient>
          <linearGradient id="grad-bars-hover" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={THEME.brand.end} stopOpacity={1} />
            <stop offset="100%" stopColor={THEME.brand.start} stopOpacity={1} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke={THEME.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="date"
          stroke={THEME.text.muted}
          tickFormatter={weekShort}
          tickLine={false}
          axisLine={false}
          fontSize={11}
        />
        <YAxis
          stroke={THEME.text.muted}
          tickFormatter={(v: number) => fmt(v)}
          tickLine={false}
          axisLine={false}
          fontSize={11}
          width={48}
        />
        <Tooltip
          cursor={{ fill: "rgba(255,255,255,0.03)" }}
          content={<BarsTooltip />}
        />
        <Bar
          dataKey="value"
          radius={[6, 6, 0, 0]}
          isAnimationActive={!reducedMotion}
          animationDuration={800}
          animationEasing="ease-out"
          animationBegin={0}
          onMouseEnter={(_, idx) => setHoverIdx(idx)}
        >
          {data.map((_, idx) => {
            const dimmed = hoverIdx !== null && hoverIdx !== idx;
            const focused = hoverIdx === idx;
            return (
              <Cell
                key={idx}
                fill={focused ? "url(#grad-bars-hover)" : "url(#grad-bars)"}
                fillOpacity={dimmed ? 0.4 : 1}
                style={{
                  transition: reducedMotion
                    ? undefined
                    : "fill-opacity 200ms ease, filter 200ms ease",
                  filter: focused
                    ? "drop-shadow(0 0 8px rgba(236,72,153,0.55))"
                    : undefined,
                }}
              />
            );
          })}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
